import { createContext, useContext, useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { useUser, useClerk } from '@clerk/clerk-react';
import { authenticatedFetch } from '../services/authService';
import { getApiUrl } from '../utils/config';
import { DollarSpinner } from '../components/DollarSpinner.jsx';

const UserSessionContext = createContext(null);

export const UserSessionProvider = ({ children }) => {
  const { isLoaded, isSignedIn } = useUser();
  const { signOut } = useClerk();
  const [session, setSession] = useState({ loading: true, user: null, subscription: null, pendingInvites: [], isAppAdmin: false, error: null });

  useEffect(() => {
    if (!isLoaded || !isSignedIn) return;
    let cancelled = false;

    (async () => {
      try {
        const res = await authenticatedFetch(`${getApiUrl()}/signin`, { method: 'POST' });
        if (!res.ok) {
          let body = null;
          try { body = await res.json(); } catch (_) { /* non-JSON */ }
          if (body && body.code === 'signup_closed') {
            // Pass redirectUrl so Clerk navigates atomically - avoids brief
            // Landing flash between signOut and a manual window.location set.
            await signOut({ redirectUrl: '/signup-closed.html' });
            return;
          }
          if (body && body.code === 'subscription_expired') {
            await signOut({ redirectUrl: '/subscription-expired.html' });
            return;
          }
          throw new Error(`signin failed: ${res.status}`);
        }
        const data = await res.json();

        // #1427: the application-admin flag rides GET /api/profile
        // (V4_ADDENDUM convention 13). Best-effort - a profile failure just
        // reports non-admin (the /api/admin/* backend gate is the real
        // enforcement), so the session never blocks on it.
        let isAppAdmin = false;
        try {
          const profileRes = await authenticatedFetch(`${getApiUrl()}/profile`, { method: 'GET' });
          if (profileRes.ok) {
            const profile = await profileRes.json();
            isAppAdmin = !!profile.isAppAdmin;
          }
        } catch (_) { /* non-admin default */ }

        if (!cancelled) {
          setSession({
            loading: false,
            user: data.user,
            subscription: data.subscription || null,
            pendingInvites: data.pendingInvites || [],
            isAppAdmin,
            error: null
          });
        }
      } catch (err) {
        if (!cancelled) setSession({ loading: false, user: null, subscription: null, pendingInvites: [], isAppAdmin: false, error: err.message });
      }
    })();

    return () => { cancelled = true; };
  }, [isLoaded, isSignedIn, signOut]);

  // The guide engine owns first-login auto-start (it has the catalog +
  // session) - see user_guides/GuidesProvider. UserSessionContext no longer
  // knows about it.

  // Hold rendering of children until /signin resolves. For closed-signup
  // users the 403 → signOut+redirect path fires during this window, so they
  // never see the app skeleton flash. Existing users see only a brief
  // spinner before their session loads.
  if (session.loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <DollarSpinner size="large" />
      </Box>
    );
  }

  return <UserSessionContext.Provider value={session}>{children}</UserSessionContext.Provider>;
};

export const useUserSession = () => {
  const ctx = useContext(UserSessionContext);
  if (!ctx) throw new Error('useUserSession must be used within UserSessionProvider');
  return ctx;
};
